// Blueprint: crap/blueprint/call-task-lifecycle/architecture/001-booking-retention.md
import appManager from '@app/appManager';
import InboxModule from '@modules/InboxModule';
import type { CloseCheck } from '@modules/InboxModule';
import { COMPLETION_IN_FLIGHT_MAX_MS } from '@modules/InboxModule/callHandlers/callStateHandler';
import { showCloseAndClassifyErrorToast } from '@modules/ToastModule/commonToasts/commonToasts';
import { inboxState } from '@modules/InboxModule/state';
import {
  RETURN_HOME,
  selectReturnCard,
} from '@modules/InboxModule/lib/selectReturnCard';
import TasksModule from '@modules/TasksModule';
import type {
  DeferredSelection,
  TaskActionOptions,
} from '@modules/TasksModule/types';
import {
  getCloseState,
  isCloseAsking,
  isGuarded,
  isLeaveAsking,
  rejoinClosePrompt,
  requestClose,
  setCloseState,
} from '../../CustomerMfeAdapter/taskCloseGuard';

// same key as InboxModule, so Close failures share its diagnostics source
const logger = appManager.logger('Inbox');

export const selectForCloseIfNeeded = (taskId: string) => {
  if (inboxState.selectedTaskId !== taskId) {
    InboxModule.selectTaskByUser(taskId);
  }
};

/** The task a confirmed Close should complete: resolved by viewKey, since the id may have been re-keyed. */
const findActiveTask = (taskId: string, viewKey: string | undefined) =>
  viewKey ? TasksModule.getTaskByViewKey(viewKey) : TasksModule.getTask(taskId);

/**
 * The card this Close returns to, as a view key: the selected card if it is not
 * the one being closed, else the previous selection; `RETURN_HOME` when that is
 * the Inbox home view. Captured at click time, before the card is selected,
 * because the shared previous-selection slot is overwritten by every other
 * Close and by any switch while the Close is checking.
 */
export const captureReturnViewKey = (taskId: string): string | undefined => {
  const returnId =
    inboxState.selectedTaskId !== taskId
      ? inboxState.selectedTaskId
      : inboxState.previousSelectedTaskId;
  if (!returnId) {
    return RETURN_HOME;
  }
  return TasksModule.getTask(returnId)?.viewKey;
};

/** A card that is gone while still selected returns to its Close's return card. */
const settleSelection = (
  task: { id: string },
  returnViewKey: string | undefined,
) => {
  if (!TasksModule.getTask(task.id) && inboxState.selectedTaskId === task.id) {
    selectReturnCard(returnViewKey);
  }
};

/** Options for the `complete` action; none when there is nothing to hand over. */
const completeOptions = (
  options: TaskActionOptions,
): TaskActionOptions | undefined => {
  const defined = Object.fromEntries(
    Object.entries(options).filter(([, value]) => value !== undefined),
  );
  return Object.keys(defined).length > 0 ? defined : undefined;
};

/**
 * `canCloseTask` bounded at the completion's own limit: the classification
 * request has no timeout, and a hung check would leave every later Close on the
 * card ignored. A timeout counts as a failed check: it shows the error toast
 * and clears the loading overlay the hung request set. The race has already
 * settled by then, so a late answer is never read, cannot open the prompt, and
 * (as an abandoned attempt) shows no "please wait" toast of its own.
 */
const checkWithinBound = async (
  taskId: string,
  viewKey: string,
): Promise<CloseCheck> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abandoned = false;
  const timeout = new Promise<CloseCheck>((resolve) => {
    timer = setTimeout(() => {
      abandoned = true;
      showCloseAndClassifyErrorToast();
      // `loading` is one shared boolean, so this also clears the overlay of any
      // other check on the task. Acceptable: it fires only after the same 60s
      // bound completion uses, when the check is presumed hung, and an
      // abandoned check cannot clear it again (`isCurrent`).
      // by view key: a late PCS fetch may have re-keyed the task while hung
      const current = TasksModule.getTaskByViewKey(viewKey);
      if (current) {
        TasksModule.setTaskLoading(current.id, false);
      }
      resolve({ status: 'blocked' });
    }, COMPLETION_IN_FLIGHT_MAX_MS);
  });
  const isCurrent = () => !abandoned && getCloseState(viewKey) === 'checking';
  try {
    return await Promise.race([
      InboxModule.canCloseTask(taskId, isCurrent),
      timeout,
    ]);
  } finally {
    clearTimeout(timer);
  }
};

const runLogged = (step: () => void) => {
  try {
    step();
  } catch (error) {
    logger.error('Close on ended call card failed', error);
  }
};

/**
 * Close on an ended voice card (booking retention on).
 *
 * Every Close stops the click reaching the card, selects the clicked card, then
 * dispatches `complete`. A card whose customer-mfe has a booking open (guarded)
 * is first checked with `canCloseTask`, then the CSR is asked: by the card's
 * own customer-mfe prompt when the CSR was looking at the card at the click,
 * else by the host's prompt, which names the card. Completing the task
 * unmounts its view, which discards the booking. Any other card (unregistered,
 * unguarded, every legacy card) goes straight to `complete`:
 * `setCallToCompleted` runs its own classification check and toast, and its
 * in-flight guard absorbs a repeat Close.
 *
 * Guarded states per card, kept outside React so InboxPage's view cap can read
 * them: idle -> checking (awaiting canCloseTask) -> handshake (customer-mfe's
 * prompt) or prompt (the host's) -> idle. A repeat Close while checking or on
 * the host prompt does nothing; on customer-mfe's prompt it only re-sends
 * `request-leave` so customer-mfe joins its open modal.
 */
export async function closeEndedCall(taskId: string): Promise<void> {
  const task = TasksModule.getTask(taskId);
  if (!task) {
    return;
  }
  const viewKey = task.viewKey;
  const returnViewKey = captureReturnViewKey(task.id);

  if (!viewKey || !isGuarded(viewKey)) {
    selectForCloseIfNeeded(task.id);
    TasksModule.actionTask(
      task.id,
      'complete',
      completeOptions({ returnViewKey }),
    );
    settleSelection(task, returnViewKey);
    return;
  }

  const state = getCloseState(viewKey);
  if (state === 'handshake') {
    rejoinClosePrompt(viewKey);
    return;
  }
  // the prompts are modal, so another card's is reachable only
  // programmatically; selecting this card would change the card behind it
  if (state !== 'idle' || isCloseAsking() || isLeaveAsking()) {
    return;
  }

  // the CSR's own prompt asks for the card they are looking at; the host's
  // names a card they can't see
  const isVisible = inboxState.selectedTaskId === task.id;
  // a blocked Close still shows the clicked card, as today's click bubble did
  selectForCloseIfNeeded(task.id);
  setCloseState(viewKey, 'checking');

  let deferred: DeferredSelection | undefined;
  let dispatched = false;
  try {
    const check = await checkWithinBound(task.id, viewKey);
    if (check.status === 'blocked') {
      return;
    }

    setCloseState(viewKey, isVisible ? 'handshake' : 'prompt');
    // requestClose releases its selection hold on every exit and hands the
    // deferred request back, so the release is done before the finally below
    const result = await requestClose(viewKey, { askMfe: isVisible });
    deferred = result.deferredSelection;

    if (result.decision === 'proceed') {
      const active = findActiveTask(task.id, viewKey);
      if (active) {
        selectForCloseIfNeeded(active.id);
        // by the task's current id, not a render-time one; the completion
        // applies the deferred request after its own restore
        TasksModule.actionTask(
          active.id,
          'complete',
          completeOptions({
            deferredSelection: deferred,
            classificationRequirement: check.requirement,
            returnViewKey,
          }),
        );
        dispatched = true;
      }
    }
  } catch (error) {
    logger.error('Close on ended call card failed', error);
  } finally {
    // each step is isolated so a throw in one cannot skip settle or leave the
    // card stuck in a non-idle Close state
    if (!dispatched) {
      runLogged(() => InboxModule.applyDeferredSelection(deferred));
    }
    runLogged(() => settleSelection(task, returnViewKey));
    setCloseState(viewKey, 'idle');
  }
}
